import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/contexts/AuthContext";
import { AdminBookingService, type AdminBookingWithDetails } from "@/lib/services/adminBookingService";
import { 
  Calendar, 
  Clock, 
  Users, 
  ArrowLeft,
  Shield,
  Trash2,
  Download,
  FileText,
  AlertTriangle
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import * as XLSX from "xlsx";

const AdminBookingHistory = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [adminBookings, setAdminBookings] = useState<AdminBookingWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterDate, setFilterDate] = useState<string>("");

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated || user?.type !== "admin") {
      navigate("/admin-dashboard");
      return;
    }

    loadAdminBookings();
  }, [isAuthenticated, user, navigate, isLoading]);

  const loadAdminBookings = async () => {
    setLoading(true);
    try {
      const bookings = await AdminBookingService.getAllAdminBookings();
      setAdminBookings(bookings);
    } catch (error) {
      console.error("Error loading admin bookings:", error);
      toast({
        title: "Error",
        description: "Failed to load admin bookings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAdminBooking = async (bookingId: string) => {
    try {
      const success = await AdminBookingService.cancelAdminBooking(bookingId);

      if (success) {
        toast({
          title: "Admin Booking Cancelled",
          description: "The admin booking has been cancelled and slots are now available.",
        });
        loadAdminBookings();
      } else {
        toast({
          title: "Cancellation Failed",
          description: "Failed to cancel admin booking. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error cancelling admin booking:", error);
      toast({
        title: "Error",
        description: "An error occurred while cancelling the admin booking.",
        variant: "destructive",
      });
    }
  };

  const exportToExcel = (data: AdminBookingWithDetails[], fileName: string) => {
    const formattedData = data.flatMap((booking) =>
      booking.slots.map((slot) => ({
        "Booking ID": booking.id,
        Date: slot.date,
        "Time Slot": formatTimeSlot(slot.time_start, slot.time_end),
        "Team Members": booking.team_members
          .map((m) => `${m.name}${m.phone_number ? ` (${m.phone_number})` : ''}`)
          .join(", "),
        "Created At": new Date(booking.created_at).toLocaleDateString(),
        Status: "Admin Booking"
      }))
    );

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Admin Bookings");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  const filteredBookings = adminBookings.filter((booking) => {
    if (!filterDate) return true;
    return booking.slots.some(slot => slot.date === filterDate);
  });

  const formatTimeSlot = (timeStart: string, timeEnd: string) => {
    const formatTime = (time: string) => {
      const [hours, minutes] = time.split(":");
      const hour = Number.parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${displayHour}:${minutes} ${ampm}`;
    };

    return `${formatTime(timeStart)} - ${formatTime(timeEnd)}`;
  };

  const groupSlotsByDate = (slots: any[]) => {
    const grouped = slots.reduce((acc, slot) => {
      if (!acc[slot.date]) {
        acc[slot.date] = [];
      }
      acc[slot.date].push(slot);
      return acc;
    }, {} as Record<string, any[]>);

    // Sort slots within each date
    Object.keys(grouped).forEach(date => {
      grouped[date].sort((a, b) => a.time_start.localeCompare(b.time_start));
    });

    return grouped;
  };

  if (isLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Loading admin booking history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/admin-dashboard")}
              className="mr-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-blue-900">Admin Booking History</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <h2 className="text-2xl font-bold text-gray-900">Admin Bookings</h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Label htmlFor="filter-date" className="whitespace-nowrap">
                Filter by Date:
              </Label>
              <Input
                id="filter-date"
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="max-w-[180px]"
              />
              {filterDate && (
                <Button
                  variant="ghost"
                  onClick={() => setFilterDate("")}
                  className="text-red-600 hover:text-red-700"
                >
                  Clear
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => exportToExcel(filteredBookings, "admin_bookings_history")}
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-600">{filteredBookings.length}</p>
                  <p className="text-sm text-gray-600">Total Admin Bookings</p>
                </div>
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-600">
                    {filteredBookings.reduce((total, booking) => total + booking.slots.length, 0)}
                  </p>
                  <p className="text-sm text-gray-600">Total Blocked Slots</p>
                </div>
                <Clock className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-600">
                    {filteredBookings.reduce((total, booking) => total + booking.team_members.length, 0)}
                  </p>
                  <p className="text-sm text-gray-600">Total Team Members</p>
                </div>
                <Users className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {filteredBookings.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Admin Bookings Found</h3>
              <p className="text-gray-600">
                {filterDate 
                  ? "No admin bookings found for the selected date." 
                  : "No admin bookings have been created yet."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {filteredBookings.map((booking) => {
              const groupedSlots = groupSlotsByDate(booking.slots);
              
              return (
                <Card key={booking.id} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-4">
                          <Badge className="bg-blue-100 text-blue-800">
                            <Shield className="w-3 h-3 mr-1" />
                            Admin Booking
                          </Badge>
                          <span className="text-sm text-gray-500">
                            {new Date(booking.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 font-mono">
                          ID: {booking.id}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleCancelAdminBooking(booking.id)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Cancel Booking
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                          <Calendar className="w-4 h-4 mr-2" />
                          Booked Slots
                        </h4>
                        <div className="space-y-3">
                          {Object.entries(groupedSlots).map(([date, slots]) => (
                            <div key={date} className="border rounded-lg p-3 bg-gray-50">
                              <div className="font-medium text-gray-900 mb-2">{date}</div>
                              <div className="grid grid-cols-2 gap-2">
                                {slots.map((slot) => (
                                  <div
                                    key={slot.id}
                                    className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded"
                                  >
                                    {formatTimeSlot(slot.time_start, slot.time_end)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                          <Users className="w-4 h-4 mr-2" />
                          Team Members ({booking.team_members.length})
                        </h4>
                        <div className="space-y-2">
                          {booking.team_members.map((member, index) => (
                            <div
                              key={index}
                              className="flex items-center p-2 bg-purple-50 border border-purple-200 rounded"
                            >
                              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-xs font-medium text-purple-700">
                                  {member.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">{member.name}</div>
                                {member.phone_number && (
                                  <div className="text-xs text-gray-600">{member.phone_number}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <Alert className="mt-4">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        <strong>Note:</strong> Cancelling this admin booking will unblock all associated slots and make them available for user bookings.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBookingHistory;